import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CreateNewFolder as CreateNewFolderIcon,
  DriveFileMove as MoveIcon,
  Search as SearchIcon,
  Source as DataSourceIcon,
  AccountTree as PipelineIcon,
  Upload as UploadIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  getCompiledAddonHostComponent,
} from '../addons/compiledAddonRegistry';
import { api } from '../utils/api';
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_LABEL } from '../config/uploadLimits';
import { dataObjectApi, DataObjectDto } from '../utils/dataObjectApi';
import {
  workspaceApi,
  WorkspaceFolder,
  WorkspacePermission,
  WorkspaceState,
} from '../utils/workspaceApi';
import { useAuth } from '../hooks/useAuth';
import { useAddons } from '../hooks/useAddons';
import { useNotify } from '../hooks/useNotify';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import ItemDetailsDrawer, { DrawerResource } from '../components/workspace/ItemDetailsDrawer';
import ExplorerColumnHeader from '../components/workspace/ExplorerColumnHeader';
import { ExplorerItemKind, KindAvatar, kindLabel } from '../components/workspace/itemKinds';

type ItemKind = 'data-object' | 'data-source' | 'pipeline';

type ExplorerItem = {
  id: string;
  key: string;
  name: string;
  kind: ItemKind;
  subtext?: string;
  modifiedAt?: string | null;
  raw?: any;
};

type ExplorerRow = {
  rowKey: string;
  kind: ExplorerItemKind;
  name: string;
  subtext?: string;
  tags: string[];
  permission: WorkspacePermission | null;
  modifiedAt?: string | null;
  folder?: WorkspaceFolder;
  item?: ExplorerItem;
  virtualRoot?: 'shared';
};

const SHARED_ROOT_ID = '__shared_with_me__';

type SortField = 'name' | 'type' | 'tags' | 'modified';

/** Category used for the Type filter; all folder-ish kinds collapse into 'folder'. */
const kindCategory = (kind: ExplorerItemKind): string =>
  kind === 'folder' || kind === 'personal-root' || kind === 'shared-root' ? 'folder' : kind;

const TYPE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'folder', label: 'Folders' },
  { value: 'data-object', label: 'Datasets' },
  { value: 'data-source', label: 'Data sources' },
  { value: 'pipeline', label: 'Pipelines' },
];

const EMPTY_WORKSPACE: WorkspaceState = {
  folders: [],
  sharedFolderIds: [],
  assignments: {},
  itemPermissions: {},
  hiddenItems: [],
  sharedItems: [],
  folderTags: {},
  itemTags: {},
  personalRootFolderId: null,
  caller: null,
};

const buildFolderTrail = (
  folders: WorkspaceFolder[],
  currentFolderId: string | null
): WorkspaceFolder[] => {
  if (!currentFolderId) {
    return [];
  }
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const trail: WorkspaceFolder[] = [];
  let pointer = currentFolderId;
  while (pointer) {
    const folder = byId.get(pointer);
    if (!folder) {
      break;
    }
    trail.unshift(folder);
    pointer = folder.parentId || '';
  }
  return trail;
};

const formatModified = (value?: string | null): string => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
};

const formatBytes = (value?: number | null): string => {
  if (value == null || value < 0) {
    return 'N/A';
  }
  if (value < 1024) {
    return `${value} B`;
  }
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = value;
  let idx = -1;
  do {
    size /= 1024;
    idx += 1;
  } while (size >= 1024 && idx < units.length - 1);
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[idx]}`;
};

const Dashboard: React.FC = () => {
  useDocumentTitle('Your Data');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken } = useAuth();
  const { workspaceHomeCardExtensions } = useAddons();
  const notify = useNotify();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [dataObjects, setDataObjects] = useState<DataObjectDto[]>([]);
  const [dataSources, setDataSources] = useState<any[]>([]);
  const [pipelines, setPipelines] = useState<any[]>([]);
  const [workspace, setWorkspace] = useState<WorkspaceState>(EMPTY_WORKSPACE);

  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState<{ field: SortField; direction: 'asc' | 'desc' }>({
    field: 'name',
    direction: 'asc',
  });
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [modifiedFilter, setModifiedFilter] = useState<{ from: string; to: string }>({
    from: '',
    to: '',
  });

  const [selectedRow, setSelectedRow] = useState<ExplorerRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openFullDataSignal, setOpenFullDataSignal] = useState(0);

  const [addOpen, setAddOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [moveDialog, setMoveDialog] = useState<{ open: boolean; itemKey?: string }>({
    open: false,
  });

  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadType, setUploadType] = useState<'CSV' | 'EXCEL' | 'JSON'>('CSV');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  // Upload progress percentage (0-100) while a large file streams, or null when idle.
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderSpace, setNewFolderSpace] = useState<'company' | 'personal'>('company');

  const [deepLinkApplied, setDeepLinkApplied] = useState(false);

  const deepLinkObjectId = searchParams.get('dataObjectId');
  const deepLinkOpenFullData = searchParams.get('openFullData') === '1';

  const refreshWorkspace = useCallback(async () => {
    try {
      const state = await workspaceApi.getState();
      setWorkspace({ ...EMPTY_WORKSPACE, ...state });
    } catch (e) {
      console.error('Failed to refresh workspace state', e);
    }
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}` };
        const [objs, sources, pipes, workspaceState] = await Promise.all([
          dataObjectApi.get<DataObjectDto[]>('/api/v1/data-objects'),
          api.get<any[]>('/api/v1/datasources', { headers }).catch(() => []),
          api.get<any[]>('/api/v1/pipelines', { headers }).catch(() => []),
          workspaceApi.getState(),
        ]);
        setDataObjects(objs || []);
        setDataSources(Array.isArray(sources) ? sources : []);
        setPipelines(Array.isArray(pipes) ? pipes : []);
        if (workspaceState) {
          let resolvedWorkspace = { ...EMPTY_WORKSPACE, ...workspaceState };
          if (!resolvedWorkspace.personalRootFolderId && resolvedWorkspace.caller?.username) {
            try {
              await workspaceApi.ensurePersonalRoot();
              resolvedWorkspace = { ...EMPTY_WORKSPACE, ...(await workspaceApi.getState()) };
            } catch (provisionError) {
              // A concurrent tab may have won the idempotent create. One read resolves that race;
              // genuine failures leave the explorer usable without a personal root.
              console.warn('Could not provision personal workspace root', provisionError);
              try {
                resolvedWorkspace = { ...EMPTY_WORKSPACE, ...(await workspaceApi.getState()) };
              } catch {
                // Keep the first successful state.
              }
            }
          }
          setWorkspace(resolvedWorkspace);
        }
      } catch (e: any) {
        console.error(e);
        setError(e?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [getToken]);

  const hiddenItemKeys = useMemo(
    () => new Set(workspace.hiddenItems || []),
    [workspace.hiddenItems]
  );

  const items: ExplorerItem[] = useMemo(() => {
    const list: ExplorerItem[] = [];
    for (const obj of dataObjects) {
      list.push({
        id: obj.id,
        key: `data-object:${obj.id}`,
        name: obj.name,
        kind: 'data-object',
        subtext: `${obj.fileType} • v${obj.version}`,
        modifiedAt: obj.updatedAt || obj.createdAt,
        raw: obj,
      });
    }
    for (const source of dataSources) {
      list.push({
        id: source.id,
        key: `data-source:${source.id}`,
        name: source.name || 'Data Source',
        kind: 'data-source',
        subtext: source.type || '',
        modifiedAt: source.updatedAt || source.createdAt,
        raw: source,
      });
    }
    for (const pipeline of pipelines) {
      list.push({
        id: pipeline.id,
        key: `pipeline:${pipeline.id}`,
        name: pipeline.name || 'Pipeline',
        kind: 'pipeline',
        subtext: pipeline.type || '',
        modifiedAt: pipeline.updatedAt || pipeline.createdAt,
        raw: pipeline,
      });
    }
    return list.filter((item) => !hiddenItemKeys.has(item.key));
  }, [dataObjects, dataSources, pipelines, hiddenItemKeys]);

  const companyDefault: WorkspacePermission =
    workspace.caller?.companyDefaultPermission || 'VIEW';

  const itemPermission = useCallback(
    (itemKey: string): WorkspacePermission =>
      workspace.itemPermissions?.[itemKey] || companyDefault,
    [workspace.itemPermissions, companyDefault]
  );

  const personalRoot = useMemo(
    () =>
      workspace.folders.find((folder) => folder.id === workspace.personalRootFolderId) || null,
    [workspace.folders, workspace.personalRootFolderId]
  );

  const sharedRoots = useMemo(
    () =>
      workspace.folders.filter(
        (folder) =>
          !folder.parentId &&
          folder.ownerUsername &&
          folder.id !== workspace.personalRootFolderId
      ),
    [workspace.folders, workspace.personalRootFolderId]
  );
  const sharedFolderIds = useMemo(
    () => new Set(workspace.sharedFolderIds || []),
    [workspace.sharedFolderIds]
  );
  const orphanSharedFolders = useMemo(
    () => workspace.folders.filter((folder) => sharedFolderIds.has(folder.id)),
    [sharedFolderIds, workspace.folders]
  );
  const sharedItemKeys = useMemo(
    () => new Set(workspace.sharedItems || []),
    [workspace.sharedItems]
  );
  const hasSharedContent =
    sharedRoots.length > 0 || orphanSharedFolders.length > 0 || sharedItemKeys.size > 0;

  const folderTrail = useMemo(
    () => buildFolderTrail(workspace.folders, currentFolderId),
    [workspace.folders, currentFolderId]
  );
  const insidePersonalTree =
    folderTrail.length > 0 && folderTrail[0].id === workspace.personalRootFolderId;
  const sharedBoundaryIds = useMemo(
    () => new Set([...sharedRoots, ...orphanSharedFolders].map((folder) => folder.id)),
    [orphanSharedFolders, sharedRoots]
  );
  const insideSharedTree =
    currentFolderId === SHARED_ROOT_ID ||
    (folderTrail.length > 0 && sharedBoundaryIds.has(folderTrail[0].id));

  const rows: ExplorerRow[] = useMemo(() => {
    const result: ExplorerRow[] = [];
    const folderRow = (folder: WorkspaceFolder, kind: ExplorerItemKind): ExplorerRow => ({
      rowKey: `folder:${folder.id}`,
      kind,
      name:
        kind === 'personal-root'
          ? 'My Workspace'
          : kind === 'shared-root'
            ? `${folder.name} (${folder.ownerUsername})`
            : folder.name,
      subtext:
        kind === 'personal-root'
          ? 'Only you can see this by default'
          : kind === 'shared-root'
            ? `Shared by ${folder.ownerUsername}`
            : undefined,
      tags: workspace.folderTags?.[folder.id] || [],
      permission: folder.permission || null,
      modifiedAt: folder.updatedAt,
      folder,
    });

    if (!currentFolderId) {
      if (personalRoot) {
        result.push(folderRow(personalRoot, 'personal-root'));
      }
      if (hasSharedContent) {
        result.push({
          rowKey: `virtual:${SHARED_ROOT_ID}`,
          kind: 'shared-root',
          name: 'Shared with me',
          subtext: 'Items and folders shared directly with you',
          tags: [],
          permission: 'VIEW',
          virtualRoot: 'shared',
        });
      }
      for (const folder of workspace.folders) {
        if (!folder.parentId && !folder.ownerUsername && !sharedFolderIds.has(folder.id)) {
          result.push(folderRow(folder, 'folder'));
        }
      }
      for (const item of items) {
        if (!workspace.assignments[item.key] && !sharedItemKeys.has(item.key)) {
          result.push({
            rowKey: item.key,
            kind: item.kind,
            name: item.name,
            subtext: item.subtext,
            tags: workspace.itemTags?.[item.key] || [],
            permission: itemPermission(item.key),
            modifiedAt: item.modifiedAt,
            item,
          });
        }
      }
    } else if (currentFolderId === SHARED_ROOT_ID) {
      for (const shared of sharedRoots) {
        result.push(folderRow(shared, 'shared-root'));
      }
      for (const folder of orphanSharedFolders) {
        result.push(folderRow(folder, 'folder'));
      }
      for (const item of items) {
        if (sharedItemKeys.has(item.key)) {
          result.push({
            rowKey: item.key,
            kind: item.kind,
            name: item.name,
            subtext: item.subtext,
            tags: workspace.itemTags?.[item.key] || [],
            permission: itemPermission(item.key),
            modifiedAt: item.modifiedAt,
            item,
          });
        }
      }
    } else {
      for (const folder of workspace.folders) {
        if (folder.parentId === currentFolderId) {
          result.push(folderRow(folder, 'folder'));
        }
      }
      for (const item of items) {
        if (workspace.assignments[item.key] === currentFolderId) {
          result.push({
            rowKey: item.key,
            kind: item.kind,
            name: item.name,
            subtext: item.subtext,
            tags: workspace.itemTags?.[item.key] || [],
            permission: itemPermission(item.key),
            modifiedAt: item.modifiedAt,
            item,
          });
        }
      }
    }

    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return result;
    }
    return result.filter(
      (row) =>
        row.name.toLowerCase().includes(query) ||
        row.tags.some((tag) => tag.toLowerCase().includes(query))
    );
  }, [
    currentFolderId,
    items,
    itemPermission,
    personalRoot,
    hasSharedContent,
    orphanSharedFolders,
    searchQuery,
    sharedRoots,
    sharedItemKeys,
    sharedFolderIds,
    workspace.assignments,
    workspace.folders,
    workspace.folderTags,
    workspace.itemTags,
  ]);

  // Tags offered in the Tags column filter: everything visible in the current folder view.
  const availableTags = useMemo(
    () => Array.from(new Set(rows.flatMap((row) => row.tags))).sort(),
    [rows]
  );

  const filtersActive =
    typeFilter.length > 0 || tagFilter.length > 0 || !!(modifiedFilter.from || modifiedFilter.to);

  const clearAllFilters = () => {
    setTypeFilter([]);
    setTagFilter([]);
    setModifiedFilter({ from: '', to: '' });
  };

  // Pinned rows (My Workspace, Shared with me) stay on top in their original order and are
  // exempt from filters; everything else sorts and filters by the active column settings.
  const displayRows = useMemo(() => {
    const pinned: ExplorerRow[] = [];
    const rest: ExplorerRow[] = [];
    for (const row of rows) {
      if (row.kind === 'personal-root' || row.virtualRoot === 'shared') {
        pinned.push(row);
      } else {
        rest.push(row);
      }
    }

    let filtered = rest;
    if (typeFilter.length > 0) {
      filtered = filtered.filter((row) => typeFilter.includes(kindCategory(row.kind)));
    }
    if (tagFilter.length > 0) {
      filtered = filtered.filter((row) => row.tags.some((tag) => tagFilter.includes(tag)));
    }
    if (modifiedFilter.from || modifiedFilter.to) {
      const fromMs = modifiedFilter.from
        ? new Date(modifiedFilter.from).getTime()
        : Number.NEGATIVE_INFINITY;
      // Include the whole "to" day.
      const toMs = modifiedFilter.to
        ? new Date(modifiedFilter.to).getTime() + 86_399_999
        : Number.POSITIVE_INFINITY;
      filtered = filtered.filter((row) => {
        if (!row.modifiedAt) {
          return false;
        }
        const ms = new Date(row.modifiedAt).getTime();
        return !Number.isNaN(ms) && ms >= fromMs && ms <= toMs;
      });
    }

    const dir = sort.direction === 'asc' ? 1 : -1;
    const byName = (a: ExplorerRow, b: ExplorerRow) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    const sorted = [...filtered].sort((a, b) => {
      switch (sort.field) {
        case 'type':
          return (kindLabel(a.kind).localeCompare(kindLabel(b.kind)) || byName(a, b)) * dir;
        case 'tags': {
          const aTag = a.tags[0] || '';
          const bTag = b.tags[0] || '';
          if (!aTag && !bTag) {
            return byName(a, b) * dir;
          }
          // Untagged rows sink to the bottom regardless of direction.
          if (!aTag) {
            return 1;
          }
          if (!bTag) {
            return -1;
          }
          return (aTag.localeCompare(bTag) || byName(a, b)) * dir;
        }
        case 'modified': {
          const aMs = a.modifiedAt ? new Date(a.modifiedAt).getTime() : Number.NaN;
          const bMs = b.modifiedAt ? new Date(b.modifiedAt).getTime() : Number.NaN;
          if (Number.isNaN(aMs) && Number.isNaN(bMs)) {
            return byName(a, b) * dir;
          }
          if (Number.isNaN(aMs)) {
            return 1;
          }
          if (Number.isNaN(bMs)) {
            return -1;
          }
          return (aMs - bMs) * dir || byName(a, b);
        }
        default:
          return byName(a, b) * dir;
      }
    });
    return [...pinned, ...sorted];
  }, [rows, sort, typeFilter, tagFilter, modifiedFilter]);

  const toggleSort = (field: SortField, direction?: 'asc' | 'desc') =>
    setSort((prev) => ({
      field,
      direction:
        direction ?? (prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'),
    }));

  const workspaceHomeCards = useMemo(
    () =>
      workspaceHomeCardExtensions.flatMap((extension) => {
        const Component = getCompiledAddonHostComponent('workspace.home.card', extension);
        return Component ? [{ extension, Component }] : [];
      }),
    [workspaceHomeCardExtensions]
  );
  const visibleWorkspaceHomeCards = currentFolderId ? [] : workspaceHomeCards;

  const toDrawerResource = useCallback(
    (row: ExplorerRow): DrawerResource => {
      if (row.folder) {
        return {
          kind: row.kind,
          id: row.folder.id,
          name: row.name,
          subtext: row.subtext,
          folder: row.folder,
        };
      }
      const item = row.item!;
      return {
        kind: item.kind,
        id: item.id,
        name: item.name,
        subtext: item.subtext,
        dataObject:
          item.kind === 'data-object'
            ? dataObjects.find((obj) => obj.id === item.id) || null
            : null,
        raw: item.raw,
      };
    },
    [dataObjects]
  );

  const openRow = (row: ExplorerRow) => {
    setSelectedRow(row);
    setDrawerOpen(true);
  };

  const enterFolder = (row: ExplorerRow) => {
    if (row.virtualRoot === 'shared') {
      setCurrentFolderId(SHARED_ROOT_ID);
      setDrawerOpen(false);
      setSelectedRow(null);
      return;
    }
    if (row.folder) {
      setCurrentFolderId(row.folder.id);
      setDrawerOpen(false);
      setSelectedRow(null);
    }
  };

  // Deep link: select (and optionally open full data for) a dataset by id.
  useEffect(() => {
    if (!deepLinkObjectId || deepLinkApplied || items.length === 0) {
      if (!deepLinkObjectId) {
        setDeepLinkApplied(true);
      }
      return;
    }
    const target = items.find(
      (item) => item.kind === 'data-object' && item.id === deepLinkObjectId
    );
    if (target) {
      const row: ExplorerRow = {
        rowKey: target.key,
        kind: target.kind,
        name: target.name,
        subtext: target.subtext,
        tags: workspace.itemTags?.[target.key] || [],
        permission: itemPermission(target.key),
        modifiedAt: target.modifiedAt,
        item: target,
      };
      setSelectedRow(row);
      setDrawerOpen(true);
      if (deepLinkOpenFullData) {
        // Give the drawer a tick to mount before signalling the dialog.
        setTimeout(() => setOpenFullDataSignal((value) => value + 1), 0);
      }
    }
    setDeepLinkApplied(true);
  }, [
    deepLinkObjectId,
    deepLinkOpenFullData,
    deepLinkApplied,
    items,
    itemPermission,
    workspace.itemTags,
  ]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      setError(null);
      if (currentFolderId) {
        await workspaceApi.createFolder({
          name: newFolderName.trim(),
          parentId: currentFolderId,
        });
      } else if (newFolderSpace === 'personal') {
        await workspaceApi.createFolder({
          name: newFolderName.trim(),
          parentId: workspace.personalRootFolderId,
          personal: !workspace.personalRootFolderId,
        });
      } else {
        await workspaceApi.createFolder({ name: newFolderName.trim(), parentId: null });
      }
      await refreshWorkspace();
      setNewFolderName('');
      setCreateFolderOpen(false);
    } catch (e: any) {
      console.error(e);
      const message = e?.response?.data?.message || e?.message || 'Failed to create folder';
      setError(message);
      notify(message, 'error');
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    try {
      setError(null);
      await workspaceApi.deleteFolder(folderId);
      if (currentFolderId === folderId) {
        setCurrentFolderId(null);
      }
      setDrawerOpen(false);
      setSelectedRow(null);
      await refreshWorkspace();
      notify('Folder deleted', 'success');
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.message || 'Failed to delete folder';
      setError(message);
      notify(message, 'error');
    }
  };

  const handleMoveItem = async (itemKey: string, folderId: string | null) => {
    try {
      setError(null);
      await workspaceApi.assignItem(itemKey, folderId);
      await refreshWorkspace();
    } catch (e: any) {
      console.error(e);
      const message = e?.response?.data?.message || e?.message || 'Failed to move item';
      setError(message);
      notify(message, 'error');
    }
  };

  const doUpload = async () => {
    if (!uploadFile || !uploadName.trim()) return;
    // Pre-flight size check against the effective (binding) ingress limit so the user
    // gets an immediate, specific error instead of streaming a doomed upload that the
    // ingress rejects with a raw 413.
    if (uploadFile.size > MAX_UPLOAD_BYTES) {
      const message = `"${uploadFile.name}" is ${formatBytes(uploadFile.size)}, which exceeds the ${MAX_UPLOAD_LABEL} upload limit. Split the file or reduce its size and try again.`;
      setUploadError(message);
      notify(message, 'error');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    try {
      const form = new FormData();
      form.append('name', uploadName.trim());
      if (uploadDescription) form.append('description', uploadDescription);
      form.append('fileType', uploadType);
      form.append('file', uploadFile);
      if (currentFolderId && currentFolderId !== SHARED_ROOT_ID) {
        form.append('workspaceFolderId', currentFolderId);
      }
      await dataObjectApi.post<DataObjectDto>('/api/v1/data-objects', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Large files can take a while; the default 30s instance timeout is too short.
        timeout: 0,
        onUploadProgress: (event) => {
          if (event.total) {
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
          }
        },
      });
      const objs = await dataObjectApi.get<DataObjectDto[]>('/api/v1/data-objects');
      setDataObjects(objs || []);
      await refreshWorkspace();
      setUploadName('');
      setUploadDescription('');
      setUploadType('CSV');
      setUploadFile(null);
      setUploadError(null);
      setUploadOpen(false);
    } catch (e: any) {
      console.error(e);
      // A 413 from the ingress returns an HTML body (no JSON message), so translate it
      // into a clear size error instead of the generic fallback.
      const message =
        e?.response?.status === 413
          ? `File exceeds the ${MAX_UPLOAD_LABEL} upload limit. Split the file or reduce its size and try again.`
          : e?.response?.data?.message || e?.message || 'Upload failed';
      // Surface inside the still-open upload dialog AND via the snackbar layer.
      setUploadError(message);
      notify(message, 'error');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const moveTargets = useMemo(() => {
    const editable = workspace.folders.filter(
      (folder) => folder.permission === 'EDIT' || folder.permission === 'MANAGE'
    );
    return editable.map((folder) => {
      const isPersonalRoot = folder.id === workspace.personalRootFolderId;
      const trail = buildFolderTrail(workspace.folders, folder.id)
        .map((node) =>
          node.id === workspace.personalRootFolderId ? 'My Workspace' : node.name
        )
        .join(' / ');
      return { id: folder.id, label: isPersonalRoot ? 'My Workspace' : trail };
    });
  }, [workspace.folders, workspace.personalRootFolderId]);

  const selectedResource = selectedRow ? toDrawerResource(selectedRow) : null;
  const selectedTags = selectedRow
    ? selectedRow.folder
      ? workspace.folderTags?.[selectedRow.folder.id] || []
      : workspace.itemTags?.[selectedRow.item!.key] || []
    : [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4">Your Data</Typography>
          <Typography variant="body2" color="text.secondary">
            Datasets, sources, pipelines and folders across your organisation.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="Search this folder…"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            disabled={currentFolderId === SHARED_ROOT_ID}
            onClick={() => {
              setNewFolderSpace(insidePersonalTree ? 'personal' : 'company');
              setCreateFolderOpen(true);
            }}
            startIcon={<CreateNewFolderIcon />}
          >
            New Folder
          </Button>
          <Button
            variant="contained"
            disabled={currentFolderId === SHARED_ROOT_ID}
            onClick={() => setAddOpen(true)}
            startIcon={<AddIcon />}
          >
            Add Data
          </Button>
        </Stack>
      </Stack>

      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2 }}
        aria-label="folder path"
      >
        <MuiLink
          component="button"
          onClick={() => {
            setCurrentFolderId(null);
            setSelectedRow(null);
            setDrawerOpen(false);
          }}
          underline="hover"
          color={currentFolderId ? 'primary' : 'text.primary'}
          sx={{ fontWeight: currentFolderId ? 400 : 600 }}
        >
          Company Workspace
        </MuiLink>
        {insideSharedTree && currentFolderId !== SHARED_ROOT_ID && (
          <MuiLink component="button" onClick={() => setCurrentFolderId(SHARED_ROOT_ID)}>
            Shared with me
          </MuiLink>
        )}
        {folderTrail.map((folder, index) => {
          const label =
            folder.id === workspace.personalRootFolderId ? 'My Workspace' : folder.name;
          return index === folderTrail.length - 1 ? (
            <Typography key={folder.id} color="text.primary" sx={{ fontWeight: 600 }}>
              {label}
            </Typography>
          ) : (
            <MuiLink
              key={folder.id}
              component="button"
              onClick={() => setCurrentFolderId(folder.id)}
              underline="hover"
            >
              {label}
            </MuiLink>
          );
        })}
        {currentFolderId === SHARED_ROOT_ID && (
          <Typography color="text.primary" sx={{ fontWeight: 600 }}>
            Shared with me
          </Typography>
        )}
      </Breadcrumbs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {visibleWorkspaceHomeCards.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {visibleWorkspaceHomeCards.map(({ extension, Component }) => (
            <Grid item xs={12} sm={6} lg={4} key={`${extension.addonId}:${extension.extensionKey}`}>
              <Component extension={extension} />
            </Grid>
          ))}
        </Grid>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="medium" aria-label="workspace items">
            <TableHead>
              <TableRow>
                <ExplorerColumnHeader
                  label="Name"
                  width="44%"
                  sortable
                  sortActive={sort.field === 'name'}
                  sortDirection={sort.direction}
                  onSort={(direction) => toggleSort('name', direction)}
                />
                <ExplorerColumnHeader
                  label="Type"
                  width="14%"
                  sortable
                  sortActive={sort.field === 'type'}
                  sortDirection={sort.direction}
                  onSort={(direction) => toggleSort('type', direction)}
                  filterActive={typeFilter.length > 0}
                  onClearFilter={() => setTypeFilter([])}
                  filterContent={
                    <FormGroup>
                      {TYPE_FILTER_OPTIONS.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          control={
                            <Checkbox
                              size="small"
                              checked={typeFilter.includes(option.value)}
                              onChange={(event) =>
                                setTypeFilter((prev) =>
                                  event.target.checked
                                    ? [...prev, option.value]
                                    : prev.filter((value) => value !== option.value)
                                )
                              }
                            />
                          }
                          label={option.label}
                        />
                      ))}
                    </FormGroup>
                  }
                />
                <ExplorerColumnHeader
                  label="Tags"
                  width="22%"
                  sortable
                  sortActive={sort.field === 'tags'}
                  sortDirection={sort.direction}
                  onSort={(direction) => toggleSort('tags', direction)}
                  filterActive={tagFilter.length > 0}
                  onClearFilter={() => setTagFilter([])}
                  filterContent={
                    availableTags.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No tags in this folder yet.
                      </Typography>
                    ) : (
                      <FormGroup sx={{ maxHeight: 240, overflowY: 'auto', flexWrap: 'nowrap' }}>
                        {availableTags.map((tag) => (
                          <FormControlLabel
                            key={tag}
                            control={
                              <Checkbox
                                size="small"
                                checked={tagFilter.includes(tag)}
                                onChange={(event) =>
                                  setTagFilter((prev) =>
                                    event.target.checked
                                      ? [...prev, tag]
                                      : prev.filter((value) => value !== tag)
                                  )
                                }
                              />
                            }
                            label={tag}
                          />
                        ))}
                      </FormGroup>
                    )
                  }
                />
                <ExplorerColumnHeader
                  label="Modified"
                  width="12%"
                  sortable
                  sortActive={sort.field === 'modified'}
                  sortDirection={sort.direction}
                  onSort={(direction) => toggleSort('modified', direction)}
                  sortAscLabel="Oldest first"
                  sortDescLabel="Newest first"
                  filterActive={!!(modifiedFilter.from || modifiedFilter.to)}
                  onClearFilter={() => setModifiedFilter({ from: '', to: '' })}
                  filterContent={
                    <Stack spacing={1}>
                      <TextField
                        label="From"
                        type="date"
                        size="small"
                        value={modifiedFilter.from}
                        onChange={(event) =>
                          setModifiedFilter((prev) => ({ ...prev, from: event.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="To"
                        type="date"
                        size="small"
                        value={modifiedFilter.to}
                        onChange={(event) =>
                          setModifiedFilter((prev) => ({ ...prev, to: event.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>
                  }
                />
                <TableCell sx={{ width: '8%' }} align="right">
                  {/* row actions */}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((row) => {
                const isFolderRow = !!row.folder || !!row.virtualRoot;
                const canMove = !isFolderRow && row.permission === 'MANAGE';
                const selected = selectedRow?.rowKey === row.rowKey && drawerOpen;
                return (
                  <TableRow
                    key={row.rowKey}
                    hover
                    selected={selected}
                    tabIndex={0}
                    onClick={() => (row.virtualRoot ? enterFolder(row) : openRow(row))}
                    onDoubleClick={() => enterFolder(row)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        if (row.virtualRoot) enterFolder(row);
                        else openRow(row);
                      }
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <KindAvatar kind={row.kind} />
                        <Box sx={{ minWidth: 0 }}>
                          {isFolderRow ? (
                            <MuiLink
                              component="button"
                              underline="hover"
                              color="text.primary"
                              onClick={(event) => {
                                event.stopPropagation();
                                enterFolder(row);
                              }}
                              sx={{ fontWeight: 600, textAlign: 'left' }}
                            >
                              {row.name}
                            </MuiLink>
                          ) : (
                            <Typography sx={{ fontWeight: 500 }} noWrap title={row.name}>
                              {row.name}
                            </Typography>
                          )}
                          {row.subtext && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {row.subtext}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {kindLabel(row.kind)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.tags.slice(0, 3).map((tag) => (
                        <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5 }} />
                      ))}
                      {row.tags.length > 3 && (
                        <Chip label={`+${row.tags.length - 3}`} size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatModified(row.modifiedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {canMove && (
                        <Tooltip title="Move to folder">
                          <IconButton
                            size="small"
                            aria-label={`Move ${row.name}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              setMoveDialog({ open: true, itemKey: row.item!.key });
                            }}
                          >
                            <MoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {displayRows.length === 0 && (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              {searchQuery || filtersActive ? 'No matches' : 'This folder is empty'}
            </Typography>
            <Typography color="text.secondary">
              {searchQuery || filtersActive
                ? 'Try a different search term or adjust the column filters.'
                : 'Add data, create a subfolder, or move existing items here.'}
            </Typography>
            {filtersActive && (
              <Button size="small" sx={{ mt: 1 }} onClick={clearAllFilters}>
                Clear all filters
              </Button>
            )}
          </Box>
        )}
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Click a row to inspect it; double-click (or click a folder name) to open a folder.
      </Typography>

      <ItemDetailsDrawer
        open={drawerOpen}
        resource={selectedResource}
        permission={selectedRow?.permission || null}
        tags={selectedTags}
        onTagsSaved={(resourceType, resourceKey, tags) => {
          setWorkspace((prev) =>
            resourceType === 'FOLDER'
              ? { ...prev, folderTags: { ...prev.folderTags, [resourceKey]: tags } }
              : { ...prev, itemTags: { ...prev.itemTags, [resourceKey]: tags } }
          );
        }}
        callerUsername={workspace.caller?.username}
        onAccessSaved={async () => {
          setDrawerOpen(false);
          setSelectedRow(null);
          const [objects, state] = await Promise.all([
            dataObjectApi.get<DataObjectDto[]>('/api/v1/data-objects'),
            workspaceApi.getState(),
          ]);
          setDataObjects(objects || []);
          setWorkspace({ ...EMPTY_WORKSPACE, ...state });
        }}
        onClose={() => setDrawerOpen(false)}
        openFullDataSignal={openFullDataSignal}
      />

      {/* Folder deletion action lives outside the drawer to keep the drawer generic. */}
      {drawerOpen &&
        selectedRow?.folder &&
        selectedRow.kind === 'folder' &&
        selectedRow.permission === 'MANAGE' && (
          <Box sx={{ position: 'fixed', bottom: 16, right: 24, zIndex: 1300 }}>
            <Button
              color="error"
              variant="outlined"
              size="small"
              onClick={() => void handleDeleteFolder(selectedRow.folder!.id)}
            >
              Delete folder
            </Button>
          </Box>
        )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Data</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => {
                setUploadError(null);
                setUploadOpen(true);
                setAddOpen(false);
              }}
            >
              Upload File (CSV / Excel / JSON)
            </Button>
            <Button
              variant="outlined"
              startIcon={<DataSourceIcon />}
              onClick={() => {
                setAddOpen(false);
                navigate('/data-sources');
              }}
            >
              Connect Data Source (DB / API)
            </Button>
            <Button
              variant="outlined"
              startIcon={<PipelineIcon />}
              onClick={() => {
                setAddOpen(false);
                navigate('/pipeline-builder/new');
              }}
            >
              Create Pipeline
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={uploadOpen}
        onClose={() => {
          setUploadError(null);
          setUploadOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Data File</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {uploadError && (
              <Alert severity="error" onClose={() => setUploadError(null)}>
                {uploadError}
              </Alert>
            )}
            {currentFolderId && currentFolderId !== SHARED_ROOT_ID && (
              <Alert severity="info">
                The file will be added to the current folder.
              </Alert>
            )}
            <TextField
              label="Name"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Description"
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              select
              label="File Type"
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value as any)}
              fullWidth
            >
              <MenuItem value="CSV">CSV</MenuItem>
              <MenuItem value="EXCEL">Excel (.xlsx)</MenuItem>
              <MenuItem value="JSON">JSON</MenuItem>
            </TextField>
            <Button component="label" variant="outlined" disabled={uploading}>
              {uploadFile ? uploadFile.name : 'Choose File'}
              <input
                type="file"
                accept=".csv,.xlsx,.json"
                hidden
                onChange={(e) => {
                  setUploadFile(e.target.files?.[0] || null);
                  setUploadError(null);
                }}
              />
            </Button>
            {uploadFile ? (
              <Typography
                variant="caption"
                color={uploadFile.size > MAX_UPLOAD_BYTES ? 'error' : 'text.secondary'}
              >
                {formatBytes(uploadFile.size)}
                {uploadFile.size > MAX_UPLOAD_BYTES
                  ? ` — exceeds the ${MAX_UPLOAD_LABEL} limit`
                  : ` of ${MAX_UPLOAD_LABEL} limit`}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                CSV, Excel (.xlsx) or JSON, up to {MAX_UPLOAD_LABEL}.
              </Typography>
            )}
            {uploading && (
              <Box>
                <LinearProgress
                  variant={uploadProgress === null ? 'indeterminate' : 'determinate'}
                  value={uploadProgress ?? undefined}
                />
                <Typography variant="caption" color="text.secondary">
                  {uploadProgress === null || uploadProgress >= 100
                    ? 'Processing…'
                    : `Uploading… ${uploadProgress}%`}
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUploadError(null);
              setUploadOpen(false);
            }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={doUpload}
            variant="contained"
            disabled={
              !uploadFile ||
              !uploadName ||
              uploading ||
              (!!uploadFile && uploadFile.size > MAX_UPLOAD_BYTES)
            }
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>New Folder</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Folder Name"
              fullWidth
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              autoFocus
            />
            {!currentFolderId ? (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Where should this folder live?
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={newFolderSpace}
                  onChange={(_event, value) => value && setNewFolderSpace(value)}
                >
                  <ToggleButton value="company">Company Workspace</ToggleButton>
                  <ToggleButton value="personal">My Workspace</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {newFolderSpace === 'company'
                    ? 'Visible to everyone in your organisation.'
                    : 'Private to you until you share it.'}
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                The folder will be created inside the current folder.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            disabled={!newFolderName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={moveDialog.open}
        onClose={() => setMoveDialog({ open: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Move to Folder</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Folder"
            value={moveDialog.itemKey ? workspace.assignments[moveDialog.itemKey] || '' : ''}
            onChange={(e) => {
              if (moveDialog.itemKey) {
                void handleMoveItem(moveDialog.itemKey, e.target.value ? e.target.value : null);
              }
              setMoveDialog({ open: false });
            }}
            helperText="Move to a folder you can edit, or back to the Company Workspace root."
            sx={{ mt: 1 }}
          >
            <MenuItem
              value=""
              disabled={
                workspace.caller?.companyDefaultPermission !== 'EDIT' &&
                workspace.caller?.companyDefaultPermission !== 'MANAGE'
              }
            >
              Company Workspace (root)
            </MenuItem>
            {moveTargets.map((target) => (
              <MenuItem key={target.id} value={target.id}>
                {target.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialog({ open: false })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
